import { Layout } from "providers/Layout";
import { useApplicantIdReportController } from "./controller";
import { FetchBoundary } from "providers/FetchBoundary";
import { useMemo, useState } from "react";
import { ApplicantCardSlice } from "./slices/ApplicantCardSlice";
import { LayoutHeader } from "./components/LayoutHeader";
import { ReportsCard } from "./components/ReportsCard";
import { Sidebar } from "views/applications-id-report/components/Sidebar";
import {
  Verification,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { cx } from "shared/utils";
import { combineVerifications } from "shared/utils";
import { CancelOrRefundModal } from "shared/app/CancelOrRefundModal";
import { PrintProvider } from "./providers/PrintProvider";
import { Redirect } from "shared/components/Redirect";
import { useAuthContext } from "providers/Auth";
import {
  filterVerificationByConfigValue,
  sortVerifications,
  verificationNameToTitle
} from "../applications-id/components/ApplicantsTable/utils";
import { VERIFICATIONS_ORDER } from "../applications-id/components/ApplicantsTable/constants";

export type TabStepsType = VerificationNames | "additional_documents" | "chat";

import logoSquare from "shared/branding/logo-square.svg";
import { RefundModal } from "../../shared/app/RefundModal";
import { Loader } from "shared/components/Loader";
import { UncancelModal } from "shared/app/UncancelModal";
import { Show } from "shared/components";

export const ApplicationsIdReportView = () => {
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showUncancelModal, setShowUncancelModal] = useState<boolean>(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const { applicationMeta, searchParams, setSearchParams, queryKey } =
    useApplicantIdReportController();

  const applicant = useMemo(() => {
    return applicationMeta.data?.applicants.length
      ? applicationMeta.data.applicants.find(
          (applicant) =>
            String(applicant.id) === String(searchParams.applicantId)
        )
      : undefined;
  }, [searchParams.applicantId, applicationMeta.data]);

  const verificationSchemas = applicant?.role
    ? applicationMeta.data?.verification_template?.verification_schemas[
        applicant.role
      ]
    : undefined;
  const applicationSchemaNames = Object.keys(verificationSchemas || {});
  // Applicant may use template that is different from the property template, which causes available reports discrepancy
  const hasVerificationsMismatch =
    (
      applicant?.verifications.filter(
        (v) => !applicationSchemaNames.includes(v.name)
      ) || []
    ).length > 0;

  const applicationVerifications = useMemo(() => {
    if (!verificationSchemas) return [];
    if (hasVerificationsMismatch && applicant?.verifications)
      return applicant.verifications;
    return Object.entries(verificationSchemas)
      .filter(([v, vValue]) =>
        filterVerificationByConfigValue({
          verificationName: v,
          verificationValue: vValue
        })
      )
      .map((v) => v[0])
      .map((name) => {
        const existingVerification = applicant?.verifications.find(
          (v) => v.name === name
        );
        if (!existingVerification)
          return {
            id: `missing-${name}`,
            name,
            title: verificationNameToTitle(name as VerificationNames),
            status: VerificationStatus.NoDataAvailable
          };
        else return existingVerification;
      });
  }, [applicant?.verifications, verificationSchemas]);

  const currentVerification = applicationVerifications.find(
    (verification) =>
      verification.name ===
      (searchParams.report || VerificationNames.RentalApplication)
  );

  const verifications = combineVerifications<Verification>(
    sortVerifications(
      (applicationVerifications as Verification[]).filter(
        (v) =>
          v.name !== VerificationNames.TermsAndConditions &&
          v.name !== VerificationNames.RentalVerification
      ),
      VERIFICATIONS_ORDER
    ),
    [
      [
        VerificationNames.IncomeVerification,
        VerificationNames.VerifiedIncomeVerification
      ]
    ]
  );

  const { type } = useAuthContext();

  if (!type.isBoomScreen) return <Redirect href="/" />;

  const isChatView = searchParams.report === "chat";

  const heightClass = isChatView
    ? ""
    : !currentVerification ||
        ![
          VerificationStatus.Pending,
          VerificationStatus.Canceled,
          VerificationStatus.Started,
          VerificationStatus.NoDataAvailable
        ].includes(currentVerification.status) ||
        currentVerification.name === VerificationNames.PetScreeningReport
      ? "min-h-full"
      : "h-full";

  if (applicationMeta.isLoading)
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader />
      </div>
    );

  if (!applicant) {
    if (!applicationMeta.data) return <Redirect href="/" />;

    return null;
  }

  return (
    <Layout
      headerClassName="bg-white border-b border-alpha-100"
      sectionIdentifier="report_section"
      header={
        <LayoutHeader
          applicationMeta={applicationMeta}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          templateId={applicationMeta.data?.verification_template?.id || ""}
        />
      }
    >
      <FetchBoundary error={applicationMeta.error}>
        <PrintProvider>
          {applicationMeta.data ? (
            <div
              className={cx(
                heightClass,
                "mx-auto flex max-w-[1200px] flex-col gap-4 print:gap-0 print:bg-white"
              )}
            >
              <ApplicantCardSlice
                applicant={applicant}
                applicationStatus={applicationMeta.data.status}
                onCancelClick={() => setShowCancelModal(true)}
                onUncancelClick={() => setShowUncancelModal(true)}
                onRefundClick={() => setShowRefundModal(true)}
                templateId={applicationMeta.data.verification_template?.id}
                verificationIds={verifications.flat().reduce((acc, curr) => {
                  if (
                    [
                      VerificationNames.IncomeVerification,
                      VerificationNames.HousingHistory
                    ].includes(curr.name)
                  )
                    acc.push(curr.id);

                  return acc;
                }, [] as number[])}
              />
              <div
                className={cx(
                  !isChatView ? "h-full" : "",
                  "flex flex-col rounded-lg border border-alpha-100 bg-white shadow-xs print:w-full print:border-none lg:flex-row print:shadow-none"
                )}
              >
                <Sidebar
                  isInvitee={!Boolean(applicant.application_started_at)}
                  application={applicationMeta.data}
                  applicantId={Number(searchParams.applicantId)}
                  currentTab={
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    (searchParams.report as VerificationNames) ||
                    VerificationNames.RentalApplication
                  }
                  tabSteps={verifications}
                  setCurrentTab={(t) => {
                    setSearchParams({ ...searchParams, report: t });
                  }}
                />
                <div
                  className={cx(
                    !isChatView ? "min-h-full md:px-6 py-4" : "",
                    "w-full print:overflow-y-visible print:border-none print:p-0 lg:border-l lg:border-alpha-40"
                  )}
                >
                  <ReportsCard
                    applicant={applicant}
                    currentTab={
                      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                      (searchParams.report as VerificationNames) ||
                      VerificationNames.RentalApplication
                    }
                    applicationMeta={applicationMeta}
                    // @ts-expect-error
                    currentVerification={currentVerification}
                    screeningCriteria={
                      // @ts-expect-error
                      applicationMeta.data.verification_template
                        .screening_criteria?.applicant_settings
                    }
                    // @ts-expect-error
                    verificationSchemas={verificationSchemas}
                  />
                </div>
              </div>

              <br />

              <div
                id="printable_documents_root"
                className="hidden print:block bg-white"
              />

              {/* Printable header/footer styles */}
              <div className="fixed top-0 hidden h-1.5 w-full justify-end px-6 print:flex">
                <img
                  alt="Boom Logo"
                  className="relative top-1 h-[30px] w-[30px]"
                  src={logoSquare}
                />
              </div>
            </div>
          ) : null}
          <CancelOrRefundModal
            applicationId={
              showCancelModal && applicationMeta.data?.id
                ? String(applicationMeta.data.id)
                : ""
            }
            applicantId={
              showCancelModal && applicant.id ? String(applicant.id) : ""
            }
            onSuccess={() => applicationMeta.refetch()}
            onClose={() => setShowCancelModal(false)}
          />
          <Show if={showUncancelModal}>
            <UncancelModal
              applicationId={
                applicationMeta.data?.id ? String(applicationMeta.data.id) : ""
              }
              applicantId={applicant.id ? String(applicant.id) : ""}
              onSuccess={() => applicationMeta.refetch()}
              onClose={() => setShowUncancelModal(false)}
            />
          </Show>
          <RefundModal
            applicationId={
              showRefundModal && applicationMeta.data?.id
                ? String(applicationMeta.data.id)
                : ""
            }
            applicantId={
              showRefundModal && applicant.id ? String(applicant.id) : ""
            }
            onClose={() => setShowRefundModal(false)}
            queryKey={queryKey}
          />
        </PrintProvider>
      </FetchBoundary>
    </Layout>
  );
};
